/**
 * The set arithmetic behind the map element's multi-select mode: toggle on a
 * click, union on a lasso, both limited to the features the owner may choose,
 * and the slot props the registered renderer reads.
 */
import React from 'react';
import type { FmStoryMapMultiSelection } from './elementMedia.types';
import type { FmMapSlotProps } from '@/shared/config/mapSlotRegistry';

/** Keeps the feature order, so the emitted set reads the same as the list. */
function inFeatureOrder(
  selection: FmStoryMapMultiSelection,
  ids: ReadonlySet<string>,
): readonly string[] {
  return selection.features.filter((feature) => ids.has(feature.id)).map((feature) => feature.id);
}

export interface FmMapSelectionModel {
  slotProps: Pick<
    FmMapSlotProps,
    'selectedFeatureIds' | 'selectableFeatureIds' | 'onFeatureSelect' | 'lasso'
  >;
  lassoActive: boolean;
  setLassoActive: (active: boolean) => void;
}

/** Slot props the multi-select renderer reads, derived from the element's selection. */
export function useMapSelectionModel(selection: FmStoryMapMultiSelection): FmMapSelectionModel {
  const [lassoActive, setLassoActive] = React.useState(false);
  const { selectedIds, onSelectionChange } = selection;
  const featureIds = React.useMemo(
    () => selection.features.map((feature) => feature.id),
    [selection.features],
  );
  const onFeatureSelect = React.useCallback(
    (featureId: string) => {
      if (!featureIds.includes(featureId)) return;
      const next = new Set(selectedIds);
      if (next.has(featureId)) next.delete(featureId);
      else next.add(featureId);
      onSelectionChange(inFeatureOrder(selection, next));
    },
    [featureIds, onSelectionChange, selectedIds, selection],
  );
  const onLassoComplete = React.useCallback(
    (enclosed: readonly string[]) => {
      const next = new Set(selectedIds);
      enclosed.filter((id) => featureIds.includes(id)).forEach((id) => next.add(id));
      onSelectionChange(inFeatureOrder(selection, next));
    },
    [featureIds, onSelectionChange, selectedIds, selection],
  );
  return {
    lassoActive,
    setLassoActive,
    slotProps: {
      selectedFeatureIds: selectedIds,
      selectableFeatureIds: featureIds,
      onFeatureSelect,
      lasso: { active: lassoActive, onActiveChange: setLassoActive, onComplete: onLassoComplete },
    },
  };
}
